import React, { useMemo } from 'react'
import {
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CRow,
  CTable,
  CTableBody,
  CTableDataCell,
  CTableHead,
  CTableHeaderCell,
  CTableRow,
} from '@coreui/react-pro'
import { cilChartLine, cilClock, cilCursor, cilLayers, cilPeople, cilTouchApp } from '@coreui/icons'
import { CChart } from '@coreui/react-chartjs'
import { useChartTheme, baseChartOptions, axis, PLATFORM_META } from './chartTheme'
import {
  ChartSkeleton,
  EmptyState,
  ErrorState,
  StatCard,
  TileSkeleton,
  capWithOther,
  computeDelta,
  formatDateLabel,
  formatDecimal,
  formatNumber,
} from './common'

/**
 * KPI cards. Each carries its number, the change against the previous period of
 * equal length, and — where a genuine per-bucket series exists — a sparkline.
 */
const KPI_TILES = [
  {
    key: 'sessions',
    label: 'App sessions',
    color: 'primary',
    icon: cilChartLine,
    format: formatNumber,
  },
  {
    key: 'active_users',
    label: 'People who opened the app',
    color: 'info',
    icon: cilPeople,
    format: formatNumber,
  },
  {
    key: 'screen_views',
    label: 'Screens viewed',
    color: 'success',
    icon: cilLayers,
    format: formatNumber,
  },
  {
    key: 'actions',
    label: 'Taps & shares',
    color: 'warning',
    icon: cilTouchApp,
    format: formatNumber,
  },
  {
    key: 'avg_screens_per_session',
    label: 'Screens per session',
    color: 'secondary',
    icon: cilCursor,
    format: (v) => formatDecimal(v, 2),
  },
  {
    key: 'avg_session_minutes',
    label: 'Average time in app',
    color: 'dark',
    icon: cilClock,
    format: (v) => `${formatDecimal(v, 1)} min`,
  },
]

const Sparkline = ({ points, color }) => {
  if (!points || points.length < 2) return null

  return (
    <CChart
      customTooltips={false}
      type="line"
      className="av2-sparkline"
      data={{
        labels: points.map((_, i) => i),
        datasets: [
          {
            label: 'Trend',
            data: points,
            borderColor: color,
            backgroundColor: 'transparent',
            borderWidth: 2,
            pointRadius: 0,
            tension: 0.4,
          },
        ],
      }}
      options={{
        maintainAspectRatio: false,
        responsive: true,
        resizeDelay: 100,
        plugins: { legend: { display: false }, tooltip: { enabled: false } },
        scales: { x: { display: false }, y: { display: false } },
        elements: { line: { borderCapStyle: 'round', borderJoinStyle: 'round' } },
      }}
    />
  )
}

const KpiRow = ({ summary, sparkSeries, theme, loading, error, onRetry }) => {
  if (loading) {
    return (
      <CRow className="g-3 mb-3">
        {KPI_TILES.map((tile) => (
          <CCol key={tile.key} xs={12} sm={6} lg={4} xxl={2}>
            <TileSkeleton />
          </CCol>
        ))}
      </CRow>
    )
  }

  if (error) {
    return (
      <CCard className="mb-3">
        <CCardBody>
          <ErrorState message={error} onRetry={onRetry} height={160} />
        </CCardBody>
      </CCard>
    )
  }

  const current = summary?.current || {}
  const previous = summary?.previous || {}

  return (
    <CRow className="g-3 mb-3">
      {KPI_TILES.map((tile) => (
        <CCol key={tile.key} xs={12} sm={6} lg={4} xxl={2}>
          <StatCard
            label={tile.label}
            value={tile.format(current[tile.key])}
            delta={computeDelta(current[tile.key], previous[tile.key])}
            icon={tile.icon}
            color={tile.color}
          >
            <Sparkline points={sparkSeries?.[tile.key]} color={theme.series[0]} />
          </StatCard>
        </CCol>
      ))}
    </CRow>
  )
}

/** Trend over time — a line, never a pie. iOS and Android as two labelled series. */
const SessionsTrend = ({ series, interval, theme, loading, error, onRetry }) => {
  const hasData = Array.isArray(series) && series.some((row) => Number(row.total) > 0)

  const data = useMemo(() => {
    if (!series) return { labels: [], datasets: [] }
    return {
      labels: series.map((row) => formatDateLabel(row.bucket)),
      datasets: PLATFORM_META.map((platform) => ({
        label: platform.label,
        data: series.map((row) => Number(row[platform.key]) || 0),
        borderColor: theme.series[platform.slot],
        backgroundColor: theme.series[platform.slot],
        borderWidth: 2,
        pointRadius: 0,
        pointHoverRadius: 5,
        pointHoverBorderWidth: 2,
        pointHoverBorderColor: theme.surface,
        tension: 0.3,
      })),
    }
  }, [series, theme])

  return (
    <CCard className="h-100">
      <CCardHeader>
        <strong>App sessions over time</strong>
        <div className="small text-medium-emphasis">
          Counted on the {interval === 'week' ? 'week' : 'day'} the session started, in the
          game&apos;s timezone
        </div>
      </CCardHeader>
      <CCardBody>
        {loading && <ChartSkeleton height={300} />}
        {!loading && error && <ErrorState message={error} onRetry={onRetry} height={300} />}
        {!loading && !error && !hasData && <EmptyState height={300} />}
        {!loading && !error && hasData && (
          <div className="av2-chart av2-chart--trend">
            <CChart
              customTooltips={false}
              type="line"
              data={data}
              options={baseChartOptions(theme, {
                scales: {
                  x: axis(theme, { grid: { display: false } }),
                  y: axis(theme, {
                    beginAtZero: true,
                    ticks: { color: theme.textMuted, precision: 0 },
                  }),
                },
              })}
            />
          </div>
        )}
      </CCardBody>
    </CCard>
  )
}

/** Parts of a whole, two categories — a donut is the right form here. */
const PlatformSplit = ({ platforms, theme, loading, error, onRetry }) => {
  const rows = useMemo(
    () => (platforms || []).filter((row) => Number(row.sessions) > 0),
    [platforms],
  )
  const hasData = rows.length > 0

  const metaFor = (deviceType) => PLATFORM_META.find((p) => p.key === deviceType)
  const colorFor = (deviceType) => {
    const meta = metaFor(deviceType)
    return meta ? theme.series[meta.slot] : theme.neutral
  }
  const labelFor = (deviceType) => {
    const meta = metaFor(deviceType)
    return meta ? meta.label : 'Unknown device'
  }

  return (
    <CCard className="h-100">
      <CCardHeader>
        <strong>iPhone vs Android</strong>
        <div className="small text-medium-emphasis">Share of sessions</div>
      </CCardHeader>
      <CCardBody>
        {loading && <ChartSkeleton height={240} />}
        {!loading && error && <ErrorState message={error} onRetry={onRetry} height={240} />}
        {!loading && !error && !hasData && <EmptyState height={240} />}
        {!loading && !error && hasData && (
          <>
            <div className="av2-chart av2-chart--donut">
              <CChart
                customTooltips={false}
                type="doughnut"
                data={{
                  labels: rows.map((row) => labelFor(row.device_type)),
                  datasets: [
                    {
                      data: rows.map((row) => Number(row.sessions)),
                      backgroundColor: rows.map((row) => colorFor(row.device_type)),
                      // A 2px surface-coloured gap does the separating, not a stroke.
                      borderColor: theme.surface,
                      borderWidth: 2,
                      hoverBorderColor: theme.surface,
                    },
                  ],
                }}
                options={baseChartOptions(theme, { cutout: '62%' })}
              />
            </div>
            {/* The numbers repeated as text, so identity never rests on colour. */}
            <CTable small borderless className="mt-3 mb-0 align-middle">
              <CTableBody>
                {rows.map((row) => (
                  <CTableRow key={row.device_type}>
                    <CTableDataCell style={{ width: 16 }}>
                      <span
                        className="av2-swatch"
                        style={{ background: colorFor(row.device_type) }}
                        aria-hidden="true"
                      />
                    </CTableDataCell>
                    <CTableDataCell>{labelFor(row.device_type)}</CTableDataCell>
                    <CTableDataCell className="text-end">
                      {formatNumber(row.sessions)}
                    </CTableDataCell>
                    <CTableDataCell className="text-end text-medium-emphasis">
                      {formatDecimal(row.share_pct, 1)}%
                    </CTableDataCell>
                  </CTableRow>
                ))}
              </CTableBody>
            </CTable>
          </>
        )}
      </CCardBody>
    </CCard>
  )
}

/** Version adoption — one stacked bar showing what share each build holds. */
const VersionAdoption = ({ versions, theme, loading, error, onRetry }) => {
  const capped = useMemo(
    () =>
      capWithOther(
        (versions || []).filter((row) => Number(row.sessions) > 0),
        5,
        ['sessions', 'users'],
        'app_version',
      ),
    [versions],
  )
  const hasData = capped.length > 0

  const colorFor = (index, row) =>
    row.__isOther ? theme.neutral : theme.series[index % theme.series.length]

  return (
    <CCard>
      <CCardHeader>
        <strong>Has the new build rolled out?</strong>
        <div className="small text-medium-emphasis">Share of sessions by app version</div>
      </CCardHeader>
      <CCardBody>
        {loading && <ChartSkeleton height={140} />}
        {!loading && error && <ErrorState message={error} onRetry={onRetry} height={140} />}
        {!loading && !error && !hasData && <EmptyState height={140} />}
        {!loading && !error && hasData && (
          <>
            <div className="av2-chart av2-chart--stack">
              <CChart
                customTooltips={false}
                type="bar"
                data={{
                  labels: ['Sessions'],
                  datasets: capped.map((row, index) => ({
                    label: row.__isOther
                      ? row.app_version
                      : `${row.app_version}${row.build_number ? ` (${row.build_number})` : ''}`,
                    data: [Number(row.sessions)],
                    backgroundColor: colorFor(index, row),
                    borderColor: theme.surface,
                    borderWidth: 2,
                    borderRadius: 4,
                    maxBarThickness: 28,
                  })),
                }}
                options={baseChartOptions(theme, {
                  indexAxis: 'y',
                  // One segment at a time. The shared default ('index') would put
                  // every version in a single tooltip, which cannot fit inside a
                  // 120px-tall canvas — and the legend plus the table below
                  // already show all of them together.
                  interaction: { mode: 'nearest', intersect: true },
                  scales: {
                    x: axis(theme, { stacked: true, display: false }),
                    y: axis(theme, { stacked: true, display: false, grid: { display: false } }),
                  },
                })}
              />
            </div>
            {/* Relief for the lighter palette slots: every value is also readable as text. */}
            <div className="av2-scroll-x">
              <CTable small borderless className="mt-2 mb-0 align-middle">
                <CTableHead>
                  <CTableRow>
                    <CTableHeaderCell colSpan={2}>Version</CTableHeaderCell>
                    <CTableHeaderCell className="text-end">Sessions</CTableHeaderCell>
                    <CTableHeaderCell className="text-end">People</CTableHeaderCell>
                    <CTableHeaderCell className="text-end">Share</CTableHeaderCell>
                  </CTableRow>
                </CTableHead>
                <CTableBody>
                  {capped.map((row, index) => (
                    <CTableRow key={`${row.app_version}-${row.build_number || index}`}>
                      <CTableDataCell style={{ width: 16 }}>
                        <span
                          className="av2-swatch"
                          style={{ background: colorFor(index, row) }}
                          aria-hidden="true"
                        />
                      </CTableDataCell>
                      <CTableDataCell>
                        {row.app_version}
                        {row.build_number && (
                          <span className="text-medium-emphasis small ms-1">
                            build {row.build_number}
                          </span>
                        )}
                      </CTableDataCell>
                      <CTableDataCell className="text-end">
                        {formatNumber(row.sessions)}
                      </CTableDataCell>
                      <CTableDataCell className="text-end">
                        {formatNumber(row.users)}
                      </CTableDataCell>
                      <CTableDataCell className="text-end text-medium-emphasis">
                        {row.share_pct !== undefined ? `${formatDecimal(row.share_pct, 1)}%` : '—'}
                      </CTableDataCell>
                    </CTableRow>
                  ))}
                </CTableBody>
              </CTable>
            </div>
          </>
        )}
      </CCardBody>
    </CCard>
  )
}

/**
 * What people are doing beyond viewing screens — taps, follows, shares.
 * A table, not a chart: these counts are small and exact, and the admin wants
 * the number plus its comparison, which a bar can't carry.
 */
const EngagementSummary = ({ engagement, loading, error, onRetry }) => {
  const rows = useMemo(() => engagement?.rows || [], [engagement])
  const hasData = rows.length > 0

  return (
    <CCard>
      <CCardHeader>
        <strong>What people are doing</strong>
        <div className="small text-medium-emphasis">
          Taps, follows and shares — everything that isn&apos;t just viewing a screen
        </div>
      </CCardHeader>
      <CCardBody>
        {loading && <ChartSkeleton height={220} />}
        {!loading && error && <ErrorState message={error} onRetry={onRetry} height={220} />}
        {!loading && !error && !hasData && (
          <EmptyState title="No taps or shares recorded in this period" height={220} />
        )}
        {!loading && !error && hasData && (
          <div className="av2-scroll-x">
            <CTable hover responsive className="align-middle mb-0">
              <CTableHead>
                <CTableRow>
                  <CTableHeaderCell>Action</CTableHeaderCell>
                  <CTableHeaderCell className="text-end">Count</CTableHeaderCell>
                  <CTableHeaderCell className="text-end">vs previous</CTableHeaderCell>
                  <CTableHeaderCell className="text-end">People</CTableHeaderCell>
                </CTableRow>
              </CTableHead>
              <CTableBody>
                {rows.map((row) => {
                  const delta = computeDelta(row.count, row.previous_count)
                  return (
                    <CTableRow key={row.event_name}>
                      <CTableDataCell>
                        <span className="av2-screen-name">{row.event_name}</span>
                      </CTableDataCell>
                      <CTableDataCell className="text-end fw-semibold">
                        {formatNumber(row.count)}
                      </CTableDataCell>
                      <CTableDataCell className={`text-end text-${delta.color}`}>
                        {delta.label}
                      </CTableDataCell>
                      <CTableDataCell className="text-end text-medium-emphasis">
                        {formatNumber(row.unique_users)}
                      </CTableDataCell>
                    </CTableRow>
                  )
                })}
              </CTableBody>
            </CTable>
          </div>
        )}
      </CCardBody>
    </CCard>
  )
}

const OverviewTab = ({
  summary,
  timeseries,
  seriesByMetric,
  platforms,
  versions,
  engagement,
  interval,
  loading,
  errors,
  onRetry,
}) => {
  const theme = useChartTheme()

  // Sparklines are drawn only where a genuine per-bucket series exists.
  //
  // /timeseries supports sessions, screen_views and active_users, so those three
  // tiles get real trend lines, and screens-per-session is derived honestly from
  // two of them. `actions` and `avg_session_minutes` have no per-bucket series —
  // those tiles show the number and its delta with no sparkline, rather than
  // borrowing another metric's shape and implying something untrue.
  const sparkSeries = useMemo(() => {
    const totals = (key) => (seriesByMetric?.[key] || []).map((row) => Number(row.total) || 0)

    const sessions = totals('sessions')
    const screenViews = totals('screen_views')

    const perSession =
      sessions.length && sessions.length === screenViews.length
        ? sessions.map((s, i) => (s > 0 ? screenViews[i] / s : 0))
        : []

    return {
      sessions,
      screen_views: screenViews,
      active_users: totals('active_users'),
      avg_screens_per_session: perSession,
    }
  }, [seriesByMetric])

  return (
    <>
      <KpiRow
        summary={summary}
        sparkSeries={sparkSeries}
        theme={theme}
        loading={loading.summary}
        error={errors.summary}
        onRetry={onRetry}
      />

      <CRow className="g-3 mb-3">
        <CCol xs={12} xl={8}>
          <SessionsTrend
            series={timeseries}
            interval={interval}
            theme={theme}
            loading={loading.timeseries}
            error={errors.timeseries}
            onRetry={onRetry}
          />
        </CCol>
        <CCol xs={12} xl={4}>
          <PlatformSplit
            platforms={platforms}
            theme={theme}
            loading={loading.platforms}
            error={errors.platforms}
            onRetry={onRetry}
          />
        </CCol>
      </CRow>

      <CRow className="g-3 mb-3">
        <CCol xs={12}>
          <VersionAdoption
            versions={versions}
            theme={theme}
            loading={loading.versions}
            error={errors.versions}
            onRetry={onRetry}
          />
        </CCol>
      </CRow>

      <CRow className="g-3">
        <CCol xs={12}>
          <EngagementSummary
            engagement={engagement}
            loading={loading.engagement}
            error={errors.engagement}
            onRetry={onRetry}
          />
        </CCol>
      </CRow>
    </>
  )
}

export default OverviewTab
